import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react'

import IconButton from '../buttons/IconButton.js'
import P from '../typography/P.js'
import Modal from './Modal.js'
import ModalHeader from './ModalHeader.js'
import {modalStyle} from './styles.js'

/** The imperative API of PdfModal, the type of its ref */
export interface PdfModalRef {
    /** Shows a base64 PDF (e.g. labels) with a download and a close button; onClose is called when it closes */
    open: (pdf: string, onClose?: () => void) => void
    close: () => void
}

const fileName = 'labels.pdf'

function pdfToBlob(pdf: string) {
    const raw = window.atob(pdf)
    const array = new Uint8Array(raw.length)

    for (let i = 0; i < raw.length; i++) {
        array[i] = raw.charCodeAt(i)
    }

    return new Blob([array], {type: 'application/pdf'})
}

/** A PDF (e.g. the printed labels) in the browser's viewer, 80% wide and 90% high; closes on a click on the backdrop */
const PdfModal = forwardRef<PdfModalRef>(function PdfModal(_props, ref) {
    const [blobUrl, setBlobUrl] = useState<string>(null)
    const onCloseRef = useRef<() => void>(null)

    // The object URL holds the PDF in memory until it is revoked: on close, on a next open and on unmount
    useEffect(() => {
        if (!blobUrl) {
            return
        }

        return () => {
            // Firefox needs a moment after a download started from it
            setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
        }
    }, [blobUrl])

    const close = () => {
        const onClose = onCloseRef.current

        onCloseRef.current = null
        setBlobUrl(null)
        onClose?.()
    }

    useImperativeHandle(ref, () => ({
        open: (pdf, onClose) => {
            onCloseRef.current = onClose || null
            setBlobUrl(URL.createObjectURL(pdfToBlob(pdf)))
        },
        close
    }), [])

    const onClickDownload = () => {
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        link.click()

        close()
    }

    return (
        <Modal
            style={{padding: 0, height: '90%', width: modalStyle.width, maxWidth: '100%'}}
            show={!!blobUrl}
            closeOnBackdropClick
            onClose={close}
        >
            <ModalHeader onClose={close} style={{marginBottom: 0}}>
                <IconButton aria-label='Downloaden' onClick={onClickDownload}>
                    <i aria-hidden className='mdi mdi-download' />
                </IconButton>
            </ModalHeader>

            <object type='application/pdf' style={{height: '100%', width: '100%', border: 'none'}} data={blobUrl}>
                <div style={{padding: 24}}>
                    <P>Deze browser ondersteunt geen PDF weergave <a href={blobUrl} target='_blank' rel='noopener noreferrer'>klik hier om het PDF bestand te openen.</a></P>
                </div>
            </object>
        </Modal>
    )
})

/** @deprecated use PdfModalRef */
export type PrintModalRef = PdfModalRef

export default PdfModal
